import { useEffect, useRef, useState } from "react";
import { useInView, motion } from "framer-motion";
import { STATS } from "@/lib/data";
import { useContent } from "@/lib/api";

const Counter = ({ value, suffix, prefix = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const duration = 1800;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return <span ref={ref}>{prefix}{n.toLocaleString()}{suffix}</span>;
};

export const Stats = () => {
  const stats = useContent("stats", STATS);
  return (
    <section className="py-20 lg:py-28 bg-secondary border-y border-border">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-border">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="p-6 lg:p-10 first:border-l-0 odd:lg:border-l-0 lg:first:border-l-0"
            >
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-clay">0{i + 1}</div>
              <div className="font-display text-5xl lg:text-7xl text-ink mt-3 leading-none tracking-[-0.04em]">
                <Counter value={s.value} suffix={s.suffix} prefix={s.prefix} />
              </div>
              <div className="font-display text-lg text-ink mt-3">{s.label}</div>
              <div className="text-sm text-muted-foreground mt-1 max-w-[24ch]">{s.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
